/*****************************************************
Naam: QuoteForm.jsx
Locatie: /Dashboard/src/components/QuoteForm.jsx
Aangeroepen door: App.js (route: /quote)
Datum: 2026-01-16
Versie: 2.0.0
Auteur: Ragnar Standhardt + Claude
******************************************************
Beschrijving:
Standalone formulier pagina waar vervoerders hun prijsopgave
kunnen invullen. Wordt geopend via link in email.

CHANGELOG v2.0.0:
- FIXED: API endpoints aangepast naar /api/quote/info en /api/quote/submit
- FIXED: Token validatie werkt nu correct
- ADDED: Goederen tabel weergave
- ADDED: Bestaande prijs waarschuwing
- IMPROVED: SendWell branding (groen thema)

Features:
- URL parameters: ?token=ABC123&vervoerder=TruckCo
- Toont aanvraag details (van, naar, datum, goederen)
- Input: Prijs All-In (EUR)
- Input: Opmerking (optioneel)
- Validatie: Prijs moet numeriek zijn
- Submit naar API
- Success/Error handling
- Mobile responsive

Workflow:
1. Vervoerder klikt link in email
2. QuoteForm laadt met aanvraag details
3. Vervoerder vult prijs in
4. Submit → API → Excel update
5. Success message
******************************************************/

import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Mail, MapPin, Calendar, TrendingUp, Package } from 'lucide-react';

const QuoteForm = () => {
  const [loading, setLoading] = useState(true);
  const [aanvraagData, setAanvraagData] = useState(null);
  const [prijs, setPrijs] = useState('');
  const [opmerking, setOpmerking] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Parse URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const vervoerder = urlParams.get('vervoerder');

  useEffect(() => {
    // Load aanvraag data
    const loadAanvraagData = async () => {
      try {
        // UPDATED: Use correct API endpoint
        const response = await fetch(`/api/quote/info?token=${encodeURIComponent(token)}`);
        const data = await response.json();
        
        if (data.success) {
          // Valideer: URL vervoerder moet matchen met token vervoerder
          const urlVervoerder = new URLSearchParams(window.location.search).get('vervoerder');
          if (urlVervoerder && data.data.vervoerder && urlVervoerder !== data.data.vervoerder) {
            setError('Ongeldige link: vervoerder komt niet overeen met token');
            return;
          }
          setAanvraagData(data.data);
          // Als er al een prijs is, vul deze in
          if (data.data.bestaandePrijs) {
            setPrijs(data.data.bestaandePrijs);
          }
        } else {
          setError(data.message || 'Ongeldige link');
        }
      } catch (err) {
        console.error('Error loading quote data:', err);
        setError('Kon aanvraag niet laden');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadAanvraagData();
    } else {
      setError('Geen geldige token');
      setLoading(false);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!prijs || isNaN(prijs) || parseFloat(prijs) <= 0) {
      setError('Vul een geldige prijs in');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // UPDATED: Use correct API endpoint
      const response = await fetch('/api/quote/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          prijs: parseFloat(prijs),
          opmerking
        })
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
      } else {
        setError(data.message || 'Fout bij versturen');
      }
    } catch (err) {
      setError('Netwerkfout - probeer opnieuw');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !aanvraagData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
        <div className="bg-white shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Fout</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              Neem contact op met SendWell voor assistentie:<br />
              <a href="mailto:info@sendwell.nl" className="text-emerald-600 hover:underline">info@sendwell.nl</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
        <div className="bg-white shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Prijsopgave Verstuurd!
            </h2>
            <p className="text-gray-600 mb-4">
              Bedankt voor uw prijsopgave. Wij nemen zo spoedig mogelijk contact met u op.
            </p>
            <div className="bg-green-50 border border-green-200 p-4">
              <p className="text-sm text-green-800">
                <strong>Aanvraag:</strong> {aanvraagData?.aanvraagID}
              </p>
              <p className="text-sm text-green-800 mt-1">
                <strong>Route:</strong> {aanvraagData?.laadPlaats} → {aanvraagData?.losPlaats}
              </p>
              <p className="text-sm text-green-800 mt-1">
                <strong>Uw prijs:</strong> €{parseFloat(prijs).toFixed(2)}
              </p>
              {opmerking && (
                <p className="text-sm text-green-800 mt-2">
                  <strong>Opmerking:</strong> {opmerking}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center p-4">
      <div className="bg-white shadow-2xl max-w-2xl w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-700 to-emerald-800 text-white p-6">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-8 h-8" />
            <h1 className="text-2xl font-bold">Prijsopgave Formulier</h1>
          </div>
          <p className="text-emerald-100">SendWell - get cargo done</p>
        </div>

        {/* Vervoerder Badge */}
        <div className="bg-emerald-600 text-white py-3 px-6 text-center font-semibold">
          {aanvraagData?.vervoerder || vervoerder}
        </div>

        {/* Aanvraag Details */}
        <div className="p-6 bg-emerald-50 border-b border-emerald-200">
          <h2 className="font-bold text-emerald-900 mb-3 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Aanvraag Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Laden */}
            <div className="bg-white p-4 border border-emerald-200">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-2">
                <MapPin className="w-4 h-4" />
                LADEN
              </div>
              <p className="text-gray-800 font-medium">
                {aanvraagData?.laadPostcode} {aanvraagData?.laadPlaats}
              </p>
              <p className="text-gray-600 text-sm">{aanvraagData?.laadLand}</p>
              <p className="text-emerald-600 text-sm mt-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(aanvraagData?.laadDatum)}
              </p>
            </div>
            
            {/* Lossen */}
            <div className="bg-white p-4 border border-emerald-200">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-2">
                <MapPin className="w-4 h-4" />
                LOSSEN
              </div>
              <p className="text-gray-800 font-medium">
                {aanvraagData?.losPostcode} {aanvraagData?.losPlaats}
              </p>
              <p className="text-gray-600 text-sm">{aanvraagData?.losLand}</p>
              <p className="text-emerald-600 text-sm mt-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(aanvraagData?.losDatum)}
              </p>
            </div>
          </div>
        </div>

        {/* Goederen */}
        {aanvraagData?.goederen && aanvraagData.goederen.length > 0 && (
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <h2 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Goederen
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-700 text-white">
                    <th className="px-3 py-2 text-left">Aantal</th>
                    <th className="px-3 py-2 text-left">Soort</th>
                    <th className="px-3 py-2 text-left">Omschrijving</th>
                    <th className="px-3 py-2 text-left">Afmetingen</th>
                    <th className="px-3 py-2 text-left">Gewicht</th>
                  </tr>
                </thead>
                <tbody>
                  {aanvraagData.goederen.map((goed, idx) => (
                    <tr key={idx} className="border-b border-gray-200">
                      <td className="px-3 py-2">{goed.aantal}</td>
                      <td className="px-3 py-2">{goed.soort}</td>
                      <td className="px-3 py-2">{goed.omschrijving || '-'}</td>
                      <td className="px-3 py-2">
                        {goed.lengte && goed.breedte && goed.hoogte 
                          ? `${goed.lengte}×${goed.breedte}×${goed.hoogte} m`
                          : '-'}
                      </td>
                      <td className="px-3 py-2">{goed.gewicht ? `${goed.gewicht} kg` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* v2.0: Prijs locked na indienen */}
            {aanvraagData?.prijsLocked ? (
              <div className="bg-green-50 border-2 border-green-400 px-6 py-6 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-green-800 mb-2">Prijs Ontvangen</h3>
                <p className="text-lg mb-2">
                  Uw prijs: <strong>€ {parseFloat(aanvraagData.bestaandePrijs).toFixed(2)}</strong>
                </p>
                <p className="text-gray-600 text-sm mb-3">
                  Uw prijs is ontvangen en wordt direct gebruikt voor de offerte aan de klant. 
                  Wijzigen is daarom niet meer mogelijk.
                </p>
                <p className="text-gray-500 text-xs">
                  Bij vragen kunt u contact opnemen met SendWell via{' '}
                  <a href="mailto:info@sendwell.nl" className="text-emerald-600 hover:underline">info@sendwell.nl</a>
                </p>
              </div>
            ) : (
            <div>

            {/* Prijs Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Prijs All-In (EUR) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-500 font-semibold">€</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={prijs}
                  onChange={(e) => setPrijs(e.target.value)}
                  className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-lg font-semibold"
                  placeholder="0.00"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Vul uw totaalprijs in (All-In, exclusief BTW)
              </p>
            </div>

            {/* Opmerking */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Opmerking (optioneel)
              </label>
              <textarea
                value={opmerking}
                onChange={(e) => setOpmerking(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                rows="3"
                placeholder="Eventuele bijzonderheden of voorwaarden..."
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            )}

            {/* Waarschuwing dat prijs definitief is */}
            <div className="bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 text-sm">
              ⚠️ <strong>Let op:</strong> Uw prijs wordt direct gebruikt voor de offerte aan de klant 
              en kan na indienen <strong>niet meer worden aangepast</strong>. 
              Controleer uw prijs zorgvuldig.
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-4 font-bold text-white text-lg transition-all transform hover:scale-105 ${
                submitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-lg'
              }`}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Versturen...
                </span>
              ) : (
                '💰 Prijs Definitief Indienen'
              )}
            </button>
          </div>
          )}
          </div>



        </form>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 text-center text-xs text-gray-500">
          SendWell © 2026 | <a href="mailto:info@sendwell.nl" className="text-emerald-600 hover:underline">info@sendwell.nl</a>
        </div>
      </div>
    </div>
  );
};

export default QuoteForm;
